import { useEffect, useMemo, useState } from "react";
import api, { apiErr } from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { ShieldAlert, Paperclip, X, Loader2, AlertTriangle } from "lucide-react";

const REGIONS = ["US", "CA", "UK", "IN", "AE", "SA", "GCC", "SG", "AU"];
const CURRENCIES = ["USD", "CAD", "GBP", "INR", "AED", "SAR", "EUR", "SGD", "AUD"];
const TERMS = ["Net 15", "Net 30", "Net 45", "Net 60", "Net 90", "On invoice"];

const EMPTY = {
  client_name: "",
  existing_client_id: null,
  client_region: "US",
  client_currency: "USD",
  poc_name: "",
  poc_email: "",
  poc_phone: "",
  poc_title: "",
  candidate_name: "",
  candidate_email: "",
  candidate_phone: "",
  position_title: "",
  salary_ctc: "",
  placement_fee: "",
  fee_type: "flat",
  payment_terms: "Net 30",
  payment_details: "",
  replacement_terms: "",
  guarantee_days: 90,
  joining_date: "",
  payment_due_date: "",
  notes: "",
  is_private: true,
};

export default function DirectPlacementDialog({ open, onOpenChange, onCreated }) {
  const [f, setF] = useState(EMPTY);
  const [dupes, setDupes] = useState([]);
  const [checking, setChecking] = useState(false);
  const [saving, setSaving] = useState(false);
  const [attachments, setAttachments] = useState([]); // [{id, filename}]
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (!open) {
      setF(EMPTY);
      setDupes([]);
      setAttachments([]);
    }
  }, [open]);

  const set = (k, v) => setF((prev) => ({ ...prev, [k]: v }));

  // Duplicate check on debounce
  useEffect(() => {
    if (!open || f.existing_client_id) return;
    const name = f.client_name.trim();
    if (name.length < 2) {
      setDupes([]);
      return;
    }
    const t = setTimeout(async () => {
      setChecking(true);
      try {
        const { data } = await api.get("/placements/direct/check-client", {
          params: { company_name: name },
        });
        setDupes(data.matches || []);
      } catch {
        setDupes([]);
      } finally {
        setChecking(false);
      }
    }, 350);
    return () => clearTimeout(t);
  }, [f.client_name, f.existing_client_id, open]);

  const attach = async (fileList) => {
    if (!fileList || !fileList.length) return;
    setUploading(true);
    const uploaded = [];
    for (const file of Array.from(fileList)) {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("record_type", "placement");
      fd.append("record_id", "pending");
      fd.append("category", "direct_placement_attachment");
      fd.append("sensitivity", "confidential");
      fd.append("description", "Direct Placement attachment");
      fd.append("tags", "direct-placement");
      try {
        const { data } = await api.post("/files/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        uploaded.push({ id: data.id, filename: data.filename || file.name });
      } catch (e) {
        toast.error(`${file.name}: ${apiErr(e)}`);
      }
    }
    setAttachments((prev) => [...prev, ...uploaded]);
    setUploading(false);
  };

  const removeAttachment = (id) =>
    setAttachments((prev) => prev.filter((a) => a.id !== id));

  const canSubmit = useMemo(
    () =>
      f.client_name.trim().length >= 2 &&
      f.poc_name.trim().length > 0 &&
      f.candidate_name.trim().length > 0 &&
      f.position_title.trim().length > 0 &&
      Number(f.placement_fee) > 0,
    [f],
  );

  const submit = async () => {
    if (!canSubmit) {
      toast.error("Client, POC, candidate, position and fee are required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...f,
        placement_fee: Number(f.placement_fee),
        salary_ctc: f.salary_ctc ? Number(f.salary_ctc) : null,
        guarantee_days: Number(f.guarantee_days) || 90,
        attachment_ids: attachments.map((a) => a.id),
        joining_date: f.joining_date ? new Date(f.joining_date).toISOString() : null,
        payment_due_date: f.payment_due_date
          ? new Date(f.payment_due_date).toISOString()
          : null,
      };
      const { data } = await api.post("/placements/direct", payload);
      toast.success(
        `Placement created${data.invoice ? ` — invoice ${data.invoice.invoice_number} drafted` : ""}`,
      );
      onCreated?.(data);
      onOpenChange(false);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.existing_client_id) {
        toast.error(detail.message || "Duplicate client");
        set("existing_client_id", detail.existing_client_id);
      } else {
        toast.error(apiErr(e));
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-3xl max-h-[92vh] overflow-y-auto scrollbar-thin"
        data-testid="direct-placement-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            Direct Placement Entry
            {f.is_private && (
              <span className="inline-flex items-center gap-1 rounded-full bg-[#c65a63]/10 text-[#c65a63] text-xs px-2 py-0.5">
                <ShieldAlert className="h-3 w-3" /> Private
              </span>
            )}
          </DialogTitle>
          <DialogDescription>
            Log a closed placement without full client onboarding. A draft invoice is
            created automatically so reporting stays in sync.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 mt-2">
          {/* Client */}
          <Section title="Client">
            <div className="grid md:grid-cols-2 gap-3">
              <Field label="Client Name *">
                <Input
                  data-testid="dp-client-name"
                  value={f.client_name}
                  onChange={(e) => {
                    set("client_name", e.target.value);
                    if (f.existing_client_id) set("existing_client_id", null);
                  }}
                  placeholder="Acme Corp"
                />
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Region">
                  <Select
                    value={f.client_region}
                    onValueChange={(v) => set("client_region", v)}
                  >
                    <SelectTrigger data-testid="dp-region">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {REGIONS.map((r) => (
                        <SelectItem key={r} value={r}>
                          {r}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Currency">
                  <Select
                    value={f.client_currency}
                    onValueChange={(v) => set("client_currency", v)}
                  >
                    <SelectTrigger data-testid="dp-currency">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CURRENCIES.map((r) => (
                        <SelectItem key={r} value={r}>
                          {r}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
              </div>
            </div>
            {dupes.length > 0 && !f.existing_client_id && (
              <div
                className="mt-2 rounded-lg border border-[#d9a441]/40 bg-[#d9a441]/10 p-3 text-sm"
                data-testid="dp-duplicate-panel"
              >
                <div className="flex items-center gap-2 font-medium mb-1">
                  <AlertTriangle className="h-4 w-4 text-[#d9a441]" />
                  {dupes.length} similar client{dupes.length > 1 ? "s" : ""} exist
                </div>
                <div className="text-xs text-muted-foreground mb-2">
                  Attach this placement to an existing record, or continue to create
                  a new one.
                </div>
                <div className="space-y-1">
                  {dupes.map((d) => (
                    <div
                      key={d.id}
                      className="flex items-center justify-between text-xs bg-background rounded px-2 py-1.5"
                    >
                      <div>
                        <span className="font-medium">{d.company_name}</span>
                        <span className="text-muted-foreground">
                          {" · "}{d.region}
                          {d.owner_name ? ` · ${d.owner_name}` : ""}
                          {d.is_shell ? " · shell" : ""}
                        </span>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          set("existing_client_id", d.id);
                          set("client_name", d.company_name);
                          toast.success(`Attached to ${d.company_name}`);
                        }}
                        data-testid={`dp-attach-${d.id}`}
                      >
                        Use this
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {f.existing_client_id && (
              <div className="mt-2 text-xs text-[#2f9e5f] flex items-center gap-2">
                Linked to existing client
                <button
                  className="underline"
                  onClick={() => set("existing_client_id", null)}
                >
                  detach
                </button>
              </div>
            )}
            {checking && (
              <div className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
                <Loader2 className="h-3 w-3 animate-spin" /> checking existing clients…
              </div>
            )}
          </Section>

          {/* POC */}
          <Section title="Client POC">
            <div className="grid md:grid-cols-2 gap-3">
              <Field label="POC Name *">
                <Input
                  data-testid="dp-poc-name"
                  value={f.poc_name}
                  onChange={(e) => set("poc_name", e.target.value)}
                />
              </Field>
              <Field label="POC Title">
                <Input
                  data-testid="dp-poc-title"
                  value={f.poc_title}
                  onChange={(e) => set("poc_title", e.target.value)}
                  placeholder="Head of Talent"
                />
              </Field>
              <Field label="POC Email">
                <Input
                  data-testid="dp-poc-email"
                  type="email"
                  value={f.poc_email}
                  onChange={(e) => set("poc_email", e.target.value)}
                />
              </Field>
              <Field label="POC Phone">
                <Input
                  data-testid="dp-poc-phone"
                  value={f.poc_phone}
                  onChange={(e) => set("poc_phone", e.target.value)}
                />
              </Field>
            </div>
          </Section>

          {/* Candidate */}
          <Section title="Candidate">
            <div className="grid md:grid-cols-2 gap-3">
              <Field label="Candidate Name *">
                <Input
                  data-testid="dp-cand-name"
                  value={f.candidate_name}
                  onChange={(e) => set("candidate_name", e.target.value)}
                />
              </Field>
              <Field label="Position / Job Title *">
                <Input
                  data-testid="dp-position"
                  value={f.position_title}
                  onChange={(e) => set("position_title", e.target.value)}
                />
              </Field>
              <Field label="Candidate Email">
                <Input
                  data-testid="dp-cand-email"
                  type="email"
                  value={f.candidate_email}
                  onChange={(e) => set("candidate_email", e.target.value)}
                />
              </Field>
              <Field label="Candidate Phone">
                <Input
                  data-testid="dp-cand-phone"
                  value={f.candidate_phone}
                  onChange={(e) => set("candidate_phone", e.target.value)}
                />
              </Field>
            </div>
          </Section>

          {/* Commercial */}
          <Section title="Commercial">
            <div className="grid md:grid-cols-2 gap-3">
              <Field label={`Salary / CTC (${f.client_currency})`}>
                <Input
                  data-testid="dp-ctc"
                  type="number"
                  value={f.salary_ctc}
                  onChange={(e) => set("salary_ctc", e.target.value)}
                />
              </Field>
              <Field label={`Placement Fee to NEXREQ (${f.client_currency}) *`}>
                <Input
                  data-testid="dp-fee"
                  type="number"
                  value={f.placement_fee}
                  onChange={(e) => set("placement_fee", e.target.value)}
                />
              </Field>
              <Field label="Fee Type">
                <Select value={f.fee_type} onValueChange={(v) => set("fee_type", v)}>
                  <SelectTrigger data-testid="dp-fee-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="flat">Flat</SelectItem>
                    <SelectItem value="percentage">Percentage of CTC</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Payment Terms">
                <Select
                  value={f.payment_terms}
                  onValueChange={(v) => set("payment_terms", v)}
                >
                  <SelectTrigger data-testid="dp-terms">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TERMS.map((t) => (
                      <SelectItem key={t} value={t}>
                        {t}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
            </div>
            <Field label="Payment Details / Instructions">
              <Textarea
                rows={2}
                data-testid="dp-payment-details"
                value={f.payment_details}
                onChange={(e) => set("payment_details", e.target.value)}
                placeholder="Bank account, PO reference, milestones…"
              />
            </Field>
            <Field label="Replacement / Warranty Terms">
              <Textarea
                rows={2}
                data-testid="dp-replacement"
                value={f.replacement_terms}
                onChange={(e) => set("replacement_terms", e.target.value)}
                placeholder="One free replacement within 90 days of joining…"
              />
            </Field>
          </Section>

          {/* Dates */}
          <Section title="Dates">
            <div className="grid md:grid-cols-3 gap-3">
              <Field label="Joining Date">
                <Input
                  data-testid="dp-joining"
                  type="date"
                  value={f.joining_date}
                  onChange={(e) => set("joining_date", e.target.value)}
                />
              </Field>
              <Field label="Payment Due Date">
                <Input
                  data-testid="dp-due"
                  type="date"
                  value={f.payment_due_date}
                  onChange={(e) => set("payment_due_date", e.target.value)}
                />
              </Field>
              <Field label="Guarantee (days)">
                <Input
                  data-testid="dp-guarantee"
                  type="number"
                  value={f.guarantee_days}
                  onChange={(e) => set("guarantee_days", e.target.value)}
                />
              </Field>
            </div>
          </Section>

          {/* Attachments */}
          <Section title="Attachments">
            <div className="flex items-center gap-3 flex-wrap">
              <label className="inline-flex items-center gap-2 rounded-lg border border-dashed border-border px-3 py-2 text-sm cursor-pointer hover:bg-accent">
                <Paperclip className="h-4 w-4" />
                <span>{uploading ? "Uploading…" : "Add files"}</span>
                <input
                  type="file"
                  multiple
                  className="hidden"
                  data-testid="dp-attach-input"
                  onChange={(e) => attach(e.target.files)}
                />
              </label>
              {attachments.map((a) => (
                <span
                  key={a.id}
                  className="inline-flex items-center gap-1.5 text-xs rounded-full bg-accent px-2 py-1"
                  data-testid={`dp-attach-chip-${a.id}`}
                >
                  {a.filename}
                  <button onClick={() => removeAttachment(a.id)}>
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))}
            </div>
          </Section>

          {/* Notes + Privacy */}
          <Section title="Notes & Privacy">
            <Textarea
              rows={3}
              data-testid="dp-notes"
              value={f.notes}
              onChange={(e) => set("notes", e.target.value)}
              placeholder="Internal notes about this placement…"
            />
            <div className="mt-3 flex items-center justify-between rounded-lg border border-[#255058]/20 bg-[#255058]/5 p-3">
              <div>
                <div className="flex items-center gap-2 font-medium text-sm">
                  <ShieldAlert className="h-4 w-4 text-[#c65a63]" />
                  Mark as Private / Restricted
                </div>
                <div className="text-xs text-muted-foreground">
                  Payment, fee, POC and commercial details will be hidden from other
                  BDMs. Super Admin retains full access and can grant/revoke.
                </div>
              </div>
              <Switch
                checked={f.is_private}
                onCheckedChange={(v) => set("is_private", v)}
                data-testid="dp-private-toggle"
              />
            </div>
          </Section>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            data-testid="dp-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={!canSubmit || saving}
            className="bg-[#255058] hover:bg-[#1c3d43] text-white"
            data-testid="dp-submit"
          >
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Create Placement
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div className="font-display font-semibold text-sm mb-2 text-[#255058]">
        {title}
      </div>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs text-muted-foreground">{label}</Label>
      {children}
    </div>
  );
}
